import classNames from "classnames";
import {
  FloatingArrow,
  FloatingFocusManager,
  FloatingPortal,
  Middleware,
  Placement,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useTransitionStyles,
} from "@floating-ui/react";
import { HTMLProps, ReactNode, RefCallback, useEffect, useRef, useState } from "react";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";

type Rect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type TriggerProps = HTMLProps<HTMLElement> & {
  ref: RefCallback<HTMLElement>;
};

export interface FloatingPopoverProps {
  trigger: (props: TriggerProps) => ReactNode;
  children: ReactNode | ((props: { dismiss: () => void }) => ReactNode);
  borderRadius?: number;
  initialOpenState?: boolean;
  placement?: Placement;
  floatingOffset?:
    | number
    | {
        mainAxis?: number;
        crossAxis?: number;
        alignmentAxis?: number | null;
      };
}

export const FloatingPopover = ({ trigger, children, borderRadius = 12, initialOpenState, placement, floatingOffset }: FloatingPopoverProps) => {
  const [open, setOpen] = useState(initialOpenState ?? false);
  const { insetPadding } = useMobilePlatform();
  const arrowRef = useRef<SVGSVGElement | null>(null);

  const BIAS_TO_CENTER_FACTOR = 0.3;
  const FLOATING_OFFSET = floatingOffset ?? 32;

  useEffect(() => {
    if (initialOpenState && initialOpenState !== open) setOpen(initialOpenState);
  }, [initialOpenState, open]);

  const biasToCenter: Middleware = {
    name: "biasToCenter",
    fn({ x, y, rects }) {
      const centerX = window.innerWidth / 2 - rects.floating.width / 2;

      return {
        x: x + (centerX - x) * BIAS_TO_CENTER_FACTOR,
        y,
      };
    },
  };

  const trackReferenceRect: Middleware = {
    name: "trackReferenceRect",
    fn({ rects }) {
      return {
        data: { reference: rects.reference },
      };
    },
  };

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: placement ?? "bottom",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(FLOATING_OFFSET),
      flip(),
      shift(),
      biasToCenter,
      trackReferenceRect,
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const click = useClick(context);
  const dismissInteraction = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismissInteraction]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 500,
    initial: { opacity: 0, transform: "scale(0.96)" },
  });

  const dismiss = () => {
    setOpen(false);
  };

  const referenceRect = isMounted ? (context.middlewareData.trackReferenceRect?.reference as DOMRect | undefined) : undefined;

  const rect: Rect | null = referenceRect
    ? { top: referenceRect.y, left: referenceRect.x, width: referenceRect.width, height: referenceRect.height }
    : null;

  const maxHeight = (() => {
    if (!rect) {
      return undefined;
    }

    const padding = 16;
    const arrowOffset = 42;
    const offsetAmount = arrowOffset + padding;

    const availableAbove = rect.top - padding - offsetAmount - (insetPadding?.top ?? 0);
    const availableBelow = window.innerHeight - (rect.top + rect.height) - padding - offsetAmount - (insetPadding?.bottom ?? 0);

    const usable = Math.max(0, Math.max(availableAbove, availableBelow));
    const minUsable = 80;

    return usable > minUsable ? usable : Math.max(usable, minUsable);
  })();

  useEffect(() => {
    if (!open) {
      document.body.style.overflow = "auto";
      return;
    }

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  const triggerNode = trigger({
    ...(getReferenceProps() as HTMLProps<HTMLElement>),
    ref: refs.setReference,
  });

  return (
    <>
      {triggerNode}

      {isMounted && (
        <FloatingPortal>
          {rect && (
            <svg
              className={classNames("pointer-events-auto", "fixed", "inset-0", "z-[9999]", "h-screen", "w-screen")}
              style={{ opacity: transitionStyles.opacity }}
              onClick={dismiss}
              data-testid="floating-popover-backdrop"
            >
              <defs>
                <mask id="floating-popover-mask" data-testid="floating-popover-mask">
                  <rect width="100%" height="100%" fill="white" />

                  <rect
                    data-testid="floating-popover-rect"
                    x={rect.left}
                    y={rect.top}
                    width={rect.width}
                    height={rect.height}
                    rx={borderRadius}
                    ry={borderRadius}
                  />
                </mask>
              </defs>

              <rect data-testid="floating-popover-overlay" width="100%" height="100%" fill="rgba(0,0,0,0.45)" mask="url(#floating-popover-mask)" />
            </svg>
          )}

          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className="z-[9999] max-w-screen px-4"
              data-testid="floating-popover-content"
              {...getFloatingProps()}
            >
              <div style={transitionStyles} className="relative rounded-md bg-white p-4 shadow-2xl">
                <FloatingArrow
                  width={32}
                  height={24}
                  tipRadius={2}
                  ref={arrowRef}
                  context={context}
                  fill="white"
                  data-testid="floating-popover-arrow"
                />

                <div
                  data-testid="floating-popover-content-wrapper"
                  style={{
                    maxHeight: maxHeight ? maxHeight : undefined,
                    overflowY: maxHeight ? "auto" : undefined,
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  {typeof children === "function" ? children({ dismiss }) : children}
                </div>
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};
